import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  runVangelderRzReplay,
  type VangelderRzReplayFileStatus,
  type VangelderRzReplayResult,
} from '../../../../../../services/admin/adminService';
import { ChevronDown, ChevronRight, FileStack } from 'lucide-react';
import type { TFn } from '../../types';

const STATUS_KEYS: Record<VangelderRzReplayFileStatus, string> = {
  preview: 'adminTesting.vangelderRzReplay.statusPreview',
  no_rz: 'adminTesting.vangelderRzReplay.statusNoRz',
  unreadable: 'adminTesting.vangelderRzReplay.statusUnreadable',
  created: 'adminTesting.vangelderRzReplay.statusCreated',
  already_stored: 'adminTesting.vangelderRzReplay.statusAlreadyStored',
  nothing_stored: 'adminTesting.vangelderRzReplay.statusNothingStored',
};

const NEUTRAL_BADGE = 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300';
const WARNING_BADGE = 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300';

/**
 * Stores only the Van Gelder sections of already-received Bidfood facturen, for a vangelder connection
 * added after those invoices arrived. Bidfood goods, adjustments and deposits are never touched.
 * Takes Gmail ids rather than AI Checker rows because old invoices may have no row in view.
 */
export function VangelderRzReplayPanel({ t }: { t: TFn }) {
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState<'cloud63' | 'cloud64'>('cloud63');
  const [idsText, setIdsText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VangelderRzReplayResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const gmailMessageIds = idsText.split(/[\s,]+/).filter(Boolean);

  const run = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm(t('adminTesting.vangelderRzReplay.applyConfirm', { count: gmailMessageIds.length }))) return;
    setLoading(true);
    setError(null);
    try {
      setResult(await runVangelderRzReplay({ account, gmailMessageIds, dryRun }));
    } catch (e: any) {
      setError(e.message || 'Failed to run Van Gelder replay');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = cn('px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);
  const disabled = loading || gmailMessageIds.length === 0;

  return (
    <div className={cn(cardClass, 'px-3 py-4 sm:px-4')}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileStack className="w-4 h-4 shrink-0 text-orange-600 dark:text-orange-400" />
          <h3 className={cn(primaryText, semiBoldText, 'truncate')}>{t('adminTesting.vangelderRzReplay.title')}</h3>
        </div>
        {open ? <ChevronDown className={cn('w-4 h-4 shrink-0', secondaryText)} /> : <ChevronRight className={cn('w-4 h-4 shrink-0', secondaryText)} />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <p className={cn(secondaryText, 'text-xs')}>{t('adminTesting.vangelderRzReplay.desc')}</p>

          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderRzReplay.accountLabel')}</label>
              <select value={account} onChange={(e) => setAccount(e.target.value as 'cloud63' | 'cloud64')} className={cn(inputCls, 'w-full')}>
                <option value="cloud63">cloud63</option>
                <option value="cloud64">cloud64</option>
              </select>
            </div>
            <div className="flex-1 min-w-[240px]">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>{t('adminTesting.vangelderRzReplay.idsLabel')}</label>
              <textarea
                rows={3}
                value={idsText}
                onChange={(e) => setIdsText(e.target.value)}
                placeholder={t('adminTesting.vangelderRzReplay.idsPlaceholder')}
                className={cn(inputCls, 'w-full font-mono')}
              />
            </div>
            <button
              onClick={() => run(true)}
              disabled={disabled}
              className={cn('px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40', primaryText, smallTextSize)}
            >
              {loading ? t('adminTesting.vangelderRzReplay.running') : t('adminTesting.vangelderRzReplay.preview')}
            </button>
            <button
              onClick={() => run(false)}
              disabled={disabled}
              className={cn('px-4 py-2 rounded-lg bg-orange-600 dark:bg-orange-600 text-white dark:text-white hover:bg-orange-700 dark:hover:bg-orange-700 disabled:opacity-40', smallTextSize)}
            >
              {t('adminTesting.vangelderRzReplay.apply')}
            </button>
          </div>

          {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>}

          {result && (
            <div className="space-y-2">
              {result.emails.map((email) => (
                <div key={email.gmailMessageId} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                  <div className={cn(secondaryText, 'text-xs font-mono break-all')}>{email.gmailMessageId}</div>
                  {email.error && <div className="text-xs text-red-700 dark:text-red-300 break-words">{email.error}</div>}
                  {!email.error && email.files.length === 0 && <div className={cn(secondaryText, 'text-xs')}>{t('adminTesting.vangelderRzReplay.noPdfs')}</div>}
                  {email.files.map((file, index) => (
                    <div key={`${file.filename}:${index}`} className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={cn(primaryText, semiBoldText)}>{file.invoiceNumber ?? file.filename}</span>
                      {file.clientName && <span className={secondaryText}>{file.clientName}</span>}
                      <span className={cn('px-2 py-0.5 rounded-full font-semibold',
                        file.status === 'unreadable' || file.status === 'nothing_stored' ? WARNING_BADGE : NEUTRAL_BADGE)}>
                        {t(STATUS_KEYS[file.status])}
                      </span>
                      {file.sections > 0 && (
                        <span className={cn(secondaryText, 'tabular-nums')}>
                          {t('adminTesting.vangelderRzReplay.counts', {
                            created: file.ordersCreated,
                            sections: file.sections,
                            total: file.rzTotal.toFixed(2),
                            facturen: file.facturenCreated,
                          })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
